import React, { useState, useEffect, useRef } from 'react';
import { getCompanyReports } from '../services/firebaseService';
import './Dashboard.css';
import { Doughnut } from 'react-chartjs-2';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

function ReportModal({ report, onClose }) {
  const reportRef = useRef(null);

  const handleDownloadPDF = async () => {
    const reportElement = reportRef.current;
    if (!reportElement) return;

    try {
      const canvas = await html2canvas(reportElement, {
        scale: 2,
        logging: false,
        useCORS: true
      });
      
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      pdf.addImage(canvas, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(canvas, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      pdf.save(`${report.title}_${new Date().toLocaleDateString()}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  if (!report) return null;
  
  return (
    <div className="report-modal-overlay">
      <div className="report-modal-content" ref={reportRef}>
        <button className="close-button" onClick={onClose}>&times;</button>
        
        <div className="report-header">
          <h2>{report.title}</h2>
          <button className="download-btn" onClick={handleDownloadPDF}>
            Download PDF
          </button>
        </div>

        <p className="report-date">
          Created: {new Date(report.createdAt).toLocaleString('no-NO', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </p>
        
        {/* Key Metrics Overview */}
        <div className="metrics-overview">
          <div className="metric-card">
            <h4>Total Users</h4>
            <p>{report.metrics.totalUsers}</p>
          </div>
          <div className="metric-card">
            <h4>Total Messages</h4>
            <p>{report.metrics.totalMessages}</p>
          </div>
          <div className="metric-card">
            <h4>Stress Indicators</h4>
            <p>{report.metrics.stressIndicators}</p>
          </div>
        </div>

        {/* Sentiment Analysis */}
        <div className="sentiment-section">
          <h3>Sentiment Analysis</h3>
          <div className="sentiment-grid">
            <div className="sentiment-card">
              <h4>Positive</h4>
              <p>{report.metrics.sentimentData.positive}</p>
            </div>
            <div className="sentiment-card">
              <h4>Neutral</h4>
              <p>{report.metrics.sentimentData.neutral}</p>
            </div>
            <div className="sentiment-card">
              <h4>Negative</h4>
              <p>{report.metrics.sentimentData.negative}</p>
            </div>
          </div>
        </div>

        {/* Weekly Activity */}
        <div className="weekly-activity-section">
          <h3>Weekly Activity</h3>
          <div className="activity-grid">
            {Object.entries(report.metrics.weeklyActivity).map(([week, count]) => (
              <div key={week} className="activity-card">
                <h4>Week {week}</h4>
                <div className="activity-bar" style={{ height: `${count * 10}px` }}>
                  {count} messages
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stress Levels Chart */}
        {report.visualizations?.stressLevels && (
          <div className="chart-section">
            <h3>Stress Level Distribution</h3>
            <div className="chart-container">
              <Doughnut 
                data={report.visualizations.stressLevels}
                options={{ 
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom'
                    }
                  }
                }}
              />
            </div>
          </div>
        )}

        {/* AI Analysis Report */}
        <div className="full-report-content">
          <h3>Detailed Analysis</h3>
          <p style={{ whiteSpace: 'pre-line' }}>{report.content}</p>
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const user = JSON.parse(sessionStorage.getItem('user'));

  useEffect(() => {
    const loadReports = async () => {
      if (user?.companyName) {
        const companyReports = await getCompanyReports(user.companyName);
        setReports(companyReports);
      }
      setLoading(false);
    };

    loadReports();
  }, [user]);

  return (
    <div className="dashboard">
      <div className="dashboard-sidebar">
        <h2>{user?.companyName || 'Company'} Dashboard</h2>
      </div>

      <div className="dashboard-main">
        <h1>Your Reports</h1>
        {loading ? (
          <div className="loading-spinner">Loading reports...</div>
        ) : reports.length === 0 ? (
          <div className="no-reports">
            <p>No reports available yet.</p>
          </div>
        ) : (
          <div className="reports-grid">
            {reports.map(report => (
              <div key={report.id} className="report-card">
                <h3>{report.title}</h3>
                <p>
                  Created: {new Date(report.createdAt).toLocaleString('no-NO', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
                <div className="report-content">
                  <p>{report.content.substring(0, 150)}...</p>
                </div>
                <button 
                  className="view-report-btn"
                  onClick={() => setSelectedReport(report)}
                >
                  View Full Report
                </button>
              </div>
            ))}
          </div>
        )}

        {selectedReport && (
          <ReportModal 
            report={selectedReport} 
            onClose={() => setSelectedReport(null)} 
          />
        )}
      </div>
    </div>
  );
}

export default Dashboard; 